"use client";

import { useRef, useSyncExternalStore } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { FaSun, FaMoon } from "react-icons/fa6";

type Theme = "light" | "dark";

const SPARKLE_GLYPHS = ["✦", "✧", "✺", "✶", "·"];

function spawnSparkles(x: number, y: number) {
    const container = document.createElement("div");
    container.style.cssText =
        "position:fixed;inset:0;pointer-events:none;z-index:9999;overflow:hidden";
    document.body.appendChild(container);

    const count = 22;
    for (let i = 0; i < count; i++) {
        const span = document.createElement("span");
        const glyph = SPARKLE_GLYPHS[Math.floor(Math.random() * SPARKLE_GLYPHS.length)];
        const hue = Math.floor(Math.random() * 360);
        span.textContent = glyph;
        span.style.cssText = `position:absolute;left:${x}px;top:${y}px;font-size:${
            10 + Math.random() * 14
        }px;color:hsl(${hue} 90% 65%);text-shadow:0 0 4px hsl(${hue} 95% 55%),0 0 12px hsl(${hue} 100% 60%),0 0 1px rgba(0,0,0,0.7),0 0 1px rgba(255,255,255,0.4);transform:translate(-50%,-50%);will-change:transform,opacity`;
        container.appendChild(span);

        const angle = Math.random() * Math.PI * 2;
        const dist = 80 + Math.random() * 220;
        const dx = Math.cos(angle) * dist;
        const dy = Math.sin(angle) * dist;
        const rot = (Math.random() - 0.5) * 720;
        const dur = 700 + Math.random() * 600;

        span.animate(
            [
                { transform: "translate(-50%,-50%) scale(0.2) rotate(0deg)", opacity: 0 },
                { transform: "translate(-50%,-50%) scale(1.1) rotate(0deg)", opacity: 1, offset: 0.15 },
                {
                    transform: `translate(calc(-50% + ${dx}px),calc(-50% + ${dy}px)) scale(0.4) rotate(${rot}deg)`,
                    opacity: 0,
                },
            ],
            { duration: dur, easing: "cubic-bezier(0.22,1,0.36,1)", fill: "forwards" },
        );
    }

    setTimeout(() => container.remove(), 1500);
}

function getSystemPref(): Theme {
    if (typeof window === "undefined") return "light";
    return window.matchMedia &&
        window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
}

function readCurrent(): Theme {
    if (typeof document === "undefined") return "light";
    const attr = document.documentElement.getAttribute("data-theme");
    if (attr === "light" || attr === "dark") return attr;
    return getSystemPref();
}

function subscribe(callback: () => void) {
    if (typeof window === "undefined") return () => {};
    const observer = new MutationObserver(callback);
    observer.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["data-theme"],
    });
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", callback);
    return () => {
        observer.disconnect();
        mq.removeEventListener("change", callback);
    };
}

export default function ThemeToggle() {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const theme = useSyncExternalStore(subscribe, readCurrent, () => "light" as Theme);
    const pathname = usePathname();
    const isMagical = pathname.endsWith("/labs/cauldron");
    const t = useTranslations("theme");

    const applyTheme = (next: Theme) => {
        document.documentElement.setAttribute("data-theme", next);
        try {
            window.localStorage.setItem("theme", next);
        } catch {}
        document.cookie = `theme=${next}; path=/; max-age=31536000; samesite=lax`;
    };

    const toggle = async () => {
        const next: Theme = theme === "dark" ? "light" : "dark";
        // The reveal below runs through WAAPI, which the CSS reduced-motion killswitch can't reach.
        const reduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

        if (reduced || typeof document.startViewTransition !== "function" || !buttonRef.current) {
            applyTheme(next);
            return;
        }

        const rect = buttonRef.current.getBoundingClientRect();
        const x = rect.left + rect.width / 2;
        const y = rect.top + rect.height / 2;

        if (isMagical) {
            spawnSparkles(x, y);
            const endRadius = Math.hypot(
                Math.max(x, window.innerWidth - x),
                Math.max(y, window.innerHeight - y),
            );
            const transition = document.startViewTransition(() => {
                applyTheme(next);
            });
            try {
                await transition.ready;
                document.documentElement.animate(
                    {
                        clipPath: [
                            `circle(0 at ${x}px ${y}px)`,
                            `circle(${endRadius}px at ${x}px ${y}px)`,
                        ],
                        filter: [
                            "blur(18px) saturate(1.8) hue-rotate(40deg) brightness(1.1)",
                            "blur(8px) saturate(1.4) hue-rotate(15deg) brightness(1.05)",
                            "blur(0) saturate(1) hue-rotate(0deg) brightness(1)",
                        ],
                    },
                    {
                        duration: 850,
                        easing: "cubic-bezier(0.22, 1, 0.36, 1)",
                        pseudoElement: "::view-transition-new(root)",
                    },
                );
            } catch {
                // ignore, theme already applied
            }
            return;
        }

        const endRadius = Math.hypot(
            Math.max(x, window.innerWidth - x),
            Math.max(y, window.innerHeight - y),
        );

        const transition = document.startViewTransition(() => {
            applyTheme(next);
        });

        try {
            await transition.ready;
            document.documentElement.animate(
                {
                    clipPath: [
                        `circle(0 at ${x}px ${y}px)`,
                        `circle(${endRadius}px at ${x}px ${y}px)`,
                    ],
                },
                {
                    duration: 500,
                    easing: "cubic-bezier(0.22, 1, 0.36, 1)",
                    pseudoElement: "::view-transition-new(root)",
                },
            );
        } catch {
            // ignore, theme already applied
        }
    };

    const isDark = theme === "dark";
    const label = t("toggleAria");

    return (
        <button
            ref={buttonRef}
            type="button"
            onClick={toggle}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            aria-label={label}
            title={label}
        >
            {isDark ? <FaSun size={16} /> : <FaMoon size={16} />}
        </button>
    );
}
